/**
 * Iconografía de la lámina. Todo es SVG inline para que el mismo dibujo sirva en
 * el mapa (como `divIcon`) y en el canvas de exportación (como imagen).
 */

import L from 'leaflet';

/** Paleta de identificación: cada DEA recibe un color distinto y estable por número. */
export const PALETTE = [
  '#e6194b',
  '#4363d8',
  '#f58231',
  '#3cb44b',
  '#911eb4',
  '#f032e6',
  '#008080',
  '#9a6324',
  '#e6b800',
  '#42d4f4',
  '#bfef45',
  '#800000',
];

/** El color identifica al DEA; el estado lo marca la línea sólida o punteada. */
export function deaColor(number) {
  return PALETTE[(number - 1) % PALETTE.length];
}

export const DASH_PROPOSED = '8,6';

/** Contenido interno del pictograma AED, sin el elemento <svg> contenedor. */
export const AED_INNER =
  '<path d="M6 3 Q17 0 28 3 L31 38 Q31 42 27 42 L7 42 Q3 42 3 38 Z" fill="#9ccc3f" stroke="#5f8a1f" stroke-width="1.5"/>' +
  '<rect x="10" y="6" width="14" height="5" rx="1" fill="#e2001a"/>' +
  '<text x="17" y="10.2" font-family="Arial" font-size="4" font-weight="700" fill="#fff" text-anchor="middle">ZOLL</text>' +
  '<text x="17" y="27" font-family="Arial" font-size="11" font-weight="800" fill="#1b6fb3" text-anchor="middle">AED</text>' +
  '<rect x="11" y="33" width="12" height="3.2" rx="1.6" fill="#3a5a12"/>' +
  '<circle cx="9" cy="38.5" r="1.4" fill="#1b6fb3"/><circle cx="25" cy="38.5" r="1.4" fill="#1b6fb3"/>';

export const AED_SVG = `<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg">${AED_INNER}</svg>`;

/** Contenido interno del pictograma de edificio, estilo línea. */
export const TOWER_INNER =
  '<rect x="2" y="4" width="20" height="30" rx="1.5" fill="rgba(16,26,36,.6)" stroke="#fff" stroke-width="2"/>' +
  '<line x1="2" y1="12" x2="22" y2="12" stroke="#fff" stroke-width="1.5"/>' +
  '<line x1="2" y1="20" x2="22" y2="20" stroke="#fff" stroke-width="1.5"/>' +
  '<line x1="2" y1="28" x2="22" y2="28" stroke="#fff" stroke-width="1.5"/>' +
  '<line x1="12" y1="4" x2="12" y2="34" stroke="#fff" stroke-width="1.5"/>';

export const TOWER_SVG = `<svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">${TOWER_INNER}</svg>`;

/** Imagen del AED lista para dibujar en canvas. Resuelve incluso si falla la carga. */
export function loadAedImage() {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(image);
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(AED_SVG)}`;
  });
}

/** Punto rojo: la ubicación exacta del DEA. Arrastrarlo mueve el círculo. */
export function deaPointIcon() {
  return L.divIcon({
    html: '<div class="deapt"></div>',
    className: '',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Punto azul: la ubicación exacta del edificio. */
export function buildingPointIcon() {
  return L.divIcon({
    html: '<div class="edpt"></div>',
    className: '',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Pictograma AED con su número. Se ancla arriba, donde engancha la línea guía. */
export function deaNumberIcon(number) {
  const html =
    '<svg width="40" height="62" viewBox="0 0 40 62" xmlns="http://www.w3.org/2000/svg">' +
    `<svg x="7" y="20" width="26" height="34" viewBox="0 0 34 44">${AED_INNER}</svg>` +
    '<circle cx="20" cy="11" r="8" fill="#14202b" stroke="#fff" stroke-width="1.6"/>' +
    `<text x="20" y="11" fill="#fff" font-size="9" font-weight="700" text-anchor="middle" dominant-baseline="central">D${number}</text>` +
    '</svg>';
  return L.divIcon({ html, className: '', iconSize: [40, 62], iconAnchor: [20, 20] });
}

/** Pictograma de edificio con su número. */
export function buildingNumberIcon(number) {
  const html =
    '<svg width="34" height="60" viewBox="0 0 34 60" xmlns="http://www.w3.org/2000/svg">' +
    `<svg x="5" y="20" width="24" height="36" viewBox="0 0 24 36">${TOWER_INNER}</svg>` +
    '<circle cx="17" cy="11" r="8" fill="#2f6db3" stroke="#fff" stroke-width="1.6"/>' +
    `<text x="17" y="11" fill="#fff" font-size="9" font-weight="700" text-anchor="middle" dominant-baseline="central">E${number}</text>` +
    '</svg>';
  return L.divIcon({ html, className: '', iconSize: [34, 60], iconAnchor: [17, 20] });
}

/** Tirador que gira la línea de radio. */
export function handleIcon() {
  return L.divIcon({ className: 'tirador', iconSize: [14, 14], iconAnchor: [7, 7] });
}

/** Vértice arrastrable del perímetro. El primero se resalta para poder cerrarlo. */
export function vertexIcon(isFirst) {
  return L.divIcon({
    className: `vhandle${isFirst ? ' first' : ''}`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    html: '',
  });
}

/** Rotación CSS del texto de la cota para que quede alineado con la línea y legible. */
export function radiusLabelRotation(bearingDeg) {
  const angle = (bearingDeg * Math.PI) / 180;
  let deg = (Math.atan2(-Math.cos(angle), Math.sin(angle)) * 180) / Math.PI;
  if (deg > 90) deg -= 180;
  if (deg < -90) deg += 180;
  return deg;
}

/** Etiqueta "R 150 m" rotada sobre la línea de radio. */
export function radiusLabelIcon(text, bearingDeg) {
  const rotation = radiusLabelRotation(bearingDeg);
  return L.divIcon({
    className: 'radlabel',
    html: `<span style="transform:rotate(${rotation.toFixed(1)}deg)">${text}</span>`,
    iconSize: [80, 24],
    iconAnchor: [40, 12],
  });
}
